import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { enqueueSnackbar } from 'notistack';
import { apiHost } from '@pv/common/api';
import { PageLoading } from '@pv/common/components';
import {
  AdminDeleteOauthApplicationMutation,
  AdminDeleteOauthApplicationMutationVariables,
  AdminOauthApplicationQuery,
  AdminOauthApplicationQueryVariables,
} from '@pv/common/graphql/operations';
import {
  ADMIN_DELETE_OAUTH_APPLICATION_MUTATION,
  ADMIN_OAUTH_APPLICATION_QUERY,
  evictOauthApplicationLists,
  oauthApplicationState,
} from './graphql';
import { OauthApplicationFormDialog } from './OauthApplicationFormDialog';

const API_HOST = apiHost(process.env.REACT_APP_PV_ENV as string);

const formatTime = (value?: string | null) =>
  value ? moment(value).format('MM/DD/YYYY h:mm A') : 'Never';

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box role="group" aria-label={label} sx={{ display: 'flex', gap: 2, py: 1 }}>
    <Typography sx={{ width: 180, flexShrink: 0 }} color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ wordBreak: 'break-all' }}>{children}</Box>
  </Box>
);

export const OauthApplicationDetails = () => {
  const { id } = useParams() as { id: string };
  const navigate = useNavigate();
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const { data } = useQuery<
    AdminOauthApplicationQuery,
    AdminOauthApplicationQueryVariables
  >(ADMIN_OAUTH_APPLICATION_QUERY, { variables: { id } });
  const [deleteOauthApplication, { loading: deleting }] = useMutation<
    AdminDeleteOauthApplicationMutation,
    AdminDeleteOauthApplicationMutationVariables
  >(ADMIN_DELETE_OAUTH_APPLICATION_MUTATION, {
    update: (cache) => {
      cache.evict({
        id: cache.identify({ __typename: 'OauthApplication', id }),
      });
      evictOauthApplicationLists(cache);
    },
  });

  const oauthApplication = data?.adminOauthApplication;
  if (!oauthApplication) {
    return <PageLoading />;
  }

  const handleDelete = async () => {
    const result = await deleteOauthApplication({
      variables: { input: { id } },
    });
    const errors = result.data?.adminDeleteOauthApplication?.errors ?? [];
    if (errors.length) {
      enqueueSnackbar(errors[0].message, { variant: 'error' });
      return;
    }
    navigate('/admin/oauth-applications');
  };

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h4">{oauthApplication.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            href={`${API_HOST}/oauth/applications/${oauthApplication.id}`}
          >
            Webhooks
          </Button>
          <Button variant="outlined" onClick={() => setEditing(true)}>
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={() => setConfirmingDelete(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>

      <Field label="Owner">
        {oauthApplication.organization ? (
          <Link
            component={RouterLink}
            to={`/admin/organizations/${oauthApplication.organization.id}/info`}
          >
            {oauthApplication.organization.name}
          </Link>
        ) : (
          'None (partner app)'
        )}
      </Field>
      <Field label="Client ID">{oauthApplication.clientId}</Field>
      <Field label="Client secret">
        {secretRevealed ? (
          oauthApplication.clientSecret
        ) : (
          <Button size="small" onClick={() => setSecretRevealed(true)}>
            Reveal
          </Button>
        )}
      </Field>
      <Field label="Redirect URI">
        {oauthApplication.redirectUri.split('\n').map((uri) => (
          <div key={uri}>{uri}</div>
        ))}
      </Field>
      <Field label="Scopes">{oauthApplication.scopes || 'Default'}</Field>
      <Field label="Confidential">
        {oauthApplication.confidential ? 'Yes' : 'No'}
      </Field>
      <Field label="State">{oauthApplicationState(oauthApplication)}</Field>
      <Field label="Contact email">
        {oauthApplication.contactEmail || 'None'}
      </Field>
      <Field label="Last token issued">
        {formatTime(oauthApplication.lastTokenIssuedAt)}
      </Field>
      <Field label="Created">{formatTime(oauthApplication.createdAt)}</Field>

      {editing && (
        <OauthApplicationFormDialog
          open
          oauthApplication={oauthApplication}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}

      <Dialog
        open={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
      >
        <DialogTitle>Delete {oauthApplication.name}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This revokes every token issued to the app and removes its webhooks.
            Integrations using it stop working right away.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingDelete(false)}>Cancel</Button>
          <Button color="error" onClick={handleDelete} disabled={deleting}>
            Delete app
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
